/* Where the live book lives, for stills that have to point a reader back to
   it — the printed edition cannot resolve a relative link. */
export const EBOOK_PUBLIC_HOST_PATH = 'slite.com/ebooks/knowledge-framework'
export const EBOOK_PUBLIC_URL = `https://${EBOOK_PUBLIC_HOST_PATH}`

export type EbookAnchorType = 'section' | 'visual' | 'interactive' | 'survey'

export type EbookAnchor = {
  id: string
  label: string
  title: string
  type: EbookAnchorType
}

export type EbookChapter = {
  id: 'cover' | `chapter-${number}`
  number: number | null
  slug: string | null
  title: string
  /* Short label for the sidebar contents list. */
  navTitle: string
  subheading?: string
  intro?: string[]
  /* Handwritten aside in the chapter header; defaults to the title. */
  note?: string
  anchors: EbookAnchor[]
}

export const ebookChapters: EbookChapter[] = [
  {
    id: 'cover',
    number: null,
    slug: null,
    title: 'The Ontology of the Company Brain',
    navTitle: 'Cover',
    subheading:
      'What 149 teams and 10+ builders taught us about giving your company a memory.',
    anchors: [],
  },
  {
    id: 'chapter-1',
    number: 1,
    slug: 'chapter-1',
    title: 'Is this... a company brain?',
    navTitle: 'What is it?',
    subheading:
      'On what is a Company Brain, what people think it is, and how it emerged.',
    intro: [
      'We surveyed 149 people about how they define a company brain, traced the term’s history, and mapped how awareness spread.',
    ],
    note: 'Is this _ a\ncompany brain?',
    anchors: [
      {
        id: 'chapter-1-what-is-a-company-brain',
        label: 'Definition',
        title: 'What is a Company Brain?',
        type: 'section',
      },
      {
        id: 'chapter-1-what-is-a-company-brain-1-ask-10-people-what',
        label: '4 definitions',
        title: 'People think the Company Brain is 4 different things',
        type: 'section',
      },
      {
        id: 'chapter-1-the-etymology-of-company-brain',
        label: 'Etymology',
        title: "The etymology of 'Company Brain'",
        type: 'section',
      },
      {
        id: 'chapter-1-the-etymology-of-company-brain-1-survey-result',
        label: 'Survey result',
        title: 'Survey result',
        type: 'survey',
      },
      {
        id: 'chapter-1-the-etymology-of-company-brain-2-four-months-later-56',
        label: '4 months later, 56%',
        title:
          "4 months after YC's definition, 56% people know the term from sources beyond X",
        type: 'section',
      },
    ],
  },
  {
    id: 'chapter-2',
    number: 2,
    slug: 'chapter-2',
    title: 'Why the continued hype?',
    navTitle: 'Why the hype?',
    subheading:
      'On fragmented context, the agents that choke on it, and who wants to own it.',
    intro: [
      'Company Brains have seen enduring hype because they solve the growing problem of context fragmentation which reached an inflection point after we started working with AI Agents.',
    ],
    anchors: [
      {
        id: 'chapter-2-your-context-is-fragmented-and-will-stay-that-way',
        label: 'Fragmented context',
        title: 'Your context is fragmented, and will stay that way.',
        type: 'section',
      },
      {
        id: 'chapter-2-your-context-is-fragmented-and-will-stay-tha-1-knowledge-systems',
        label: 'Knowledge systems',
        title: 'Knowledge systems',
        type: 'survey',
      },
      {
        id: 'chapter-2-your-context-is-fragmented-and-will-stay-tha-4-documentation-lifecycle',
        label: 'Broken promise',
        title: "Documentation's broken promise",
        type: 'section',
      },
      {
        id: 'chapter-2-your-context-is-fragmented-and-will-stay-tha-6-documentation-lifecycle',
        label: 'Documentation decay',
        title: 'Documentation decay by release cadence',
        type: 'survey',
      },
      {
        id: 'chapter-2-this-is-why-most-survey-respondents-wanted-a-company-brain-for-their-t',
        label: 'Humans first',
        title:
          "This is why most survey respondents wanted a company brain for their team's humans first.",
        type: 'section',
      },
      {
        id: 'chapter-2-this-is-why-most-survey-respondents-wanted-a-0-human-demand',
        label: 'Human demand',
        title: 'Human demand',
        type: 'survey',
      },
      {
        id: 'chapter-2-agents-amplify-every-problem-humans-faced-with-stale-documentation',
        label: 'Agent consequences',
        title:
          'Agents amplify every problem humans faced with stale documentation',
        type: 'section',
      },
      {
        id: 'chapter-2-agents-amplify-every-problem-humans-faced-wi-4-human-vs-agent-impact',
        label: 'Human vs agent impact',
        title: 'Human vs agent impact',
        type: 'visual',
      },
    ],
  },
  {
    id: 'chapter-3',
    number: 3,
    slug: 'chapter-3',
    title: 'Applications of a Company Brain',
    navTitle: 'Applications',
    subheading:
      'On the quick wins for humans, and the kinds of agents a brain unlocks.',
    intro: [
      'A Company Brain unlocks clean, accurate context which dramatically changes how we work with each other, or the kind of work we can trust our AI Agents to do.',
    ],
    anchors: [
      {
        id: 'chapter-3-the-company-brain-gives-context-to-the-whole-company',
        label: 'Context for all',
        title: 'The Company Brain gives context to the whole company.',
        type: 'section',
      },
      {
        id: 'chapter-3-the-company-brain-gives-context-to-the-whole-2-human-quick-wins',
        label: 'Human quick wins',
        title:
          'For humans, the brain replaces the longest part of every task: assembling context.',
        type: 'section',
      },
      {
        id: 'chapter-3-for-agents-the-brain-is-the-difference-between-useful-and-dangerous',
        label: 'Useful vs dangerous',
        title:
          'For agents, the brain is the difference between useful and dangerous',
        type: 'section',
      },
      {
        id: 'chapter-3-for-agents-the-brain-is-the-difference-betwe-1-ai-fluency',
        label: 'AI fluency',
        title: 'AI fluency',
        type: 'survey',
      },
      {
        id: 'chapter-3-for-agents-the-brain-is-the-difference-betwe-3-eight-agent-types',
        label: 'Eight agent types',
        title: 'Eight agent types',
        type: 'visual',
      },
    ],
  },
  {
    id: 'chapter-4',
    number: 4,
    slug: 'chapter-4',
    title: 'The Architecture of a Company Brain',
    navTitle: 'Architecture',
    subheading:
      'On the four components every brain shares, and the ways to build each one.',
    intro: [
      'Under the hood, Company Brains differ in their architecture by orders of magnitude. However, each one decomposes into the same four components.',
    ],
    anchors: [
      {
        id: 'chapter-4-every-company-brain-has-the-same-four-components',
        label: 'Four components',
        title: 'Every company brain has the same four components.',
        type: 'section',
      },
      {
        id: 'chapter-4-every-company-brain-has-the-same-four-compon-2-architecture-overview',
        label: 'Architecture overview',
        title: 'Architecture overview',
        type: 'visual',
      },
      {
        id: 'chapter-4-getting-signals',
        label: 'Getting signals',
        title: 'Getting signals',
        type: 'section',
      },
      {
        id: 'chapter-4-remembering',
        label: 'Remembering',
        title: 'Remembering',
        type: 'section',
      },
      {
        id: 'chapter-4-remembering-4-memory-models',
        label: 'Memory models',
        title: 'Memory models',
        type: 'visual',
      },
      {
        id: 'chapter-4-dreaming-and-pruning',
        label: 'Dreaming and pruning',
        title: 'Dreaming and pruning',
        type: 'section',
      },
      {
        id: 'chapter-4-speaking-and-searching',
        label: 'Speaking and searching',
        title: 'Speaking and searching',
        type: 'section',
      },
      {
        id: 'chapter-4-one-brain-many-ways-to-build-it',
        label: 'Ways to build',
        title: 'One brain, many ways to build it',
        type: 'section',
      },
    ],
  },
  {
    id: 'chapter-5',
    number: 5,
    slug: 'chapter-5',
    title: 'Visualising real architectures from the field',
    navTitle: 'Real brains',
    subheading: 'Visualising real architectures from the field',
    intro: [
      'Breaking down the architectures of the most popular open-source and closed-source brains that are being used by real users for their own teams today.',
    ],
    note: 'What are the most popular technical\narchitectures in the market?',
    anchors: [
      {
        id: 'chapter-5-inside-real-company-brains',
        label: 'Real brains',
        title: 'Visualising real architectures from the field',
        type: 'section',
      },
      {
        id: 'chapter-5-inside-real-company-brains-1-vendor-chips',
        label: 'Vendor chips',
        title: 'Nine company brains, four ports',
        type: 'visual',
      },
    ],
  },
  /* Chapters below were renumbered when "Inside real company brains" became
     its own chapter — their anchor ids keep the old chapter prefix on purpose
     (anchor ids are permalinks; renaming them breaks shared deep links). */
  {
    id: 'chapter-6',
    number: 6,
    slug: 'chapter-6',
    title: 'Where the market is',
    navTitle: 'Market',
    subheading:
      "On who's building, who got burned, what everyone fears, and which brain you actually need.",
    intro: [
      "Beyond the hype, this is a lay of the land of how many people have tried a Company Brain, or actively evaluating one, and exactly what they're looking for.",
    ],
    anchors: [
      {
        id: 'chapter-5-only-17-people-have-a-company-brain-that-works',
        label: 'Adoption stats',
        title: 'Only 17% people have a company brain that works.',
        type: 'section',
      },
      {
        id: 'chapter-5-the-top-5-concerns-about-adopting-a-company-brain',
        label: 'Top concerns',
        title: 'The top 5 concerns about adopting a Company Brain',
        type: 'section',
      },
      {
        id: 'chapter-5-the-top-5-concerns-about-adopting-a-company-brain-0-graph',
        label: 'Concerns',
        title: 'Biggest concerns about adopting a company brain',
        type: 'visual',
      },
      {
        id: 'chapter-5-the-map',
        label: 'Market map',
        title: 'The map of current Company Brain solutions',
        type: 'section',
      },
      {
        id: 'chapter-5-the-map-3-context-sovereignty',
        label: 'Context sovereignty',
        title: 'One last thing: You should own your context',
        type: 'section',
      },
      {
        id: 'chapter-5-the-map-12-treat-context-sovereignty-as',
        label: 'Treat context sovereignty as',
        title:
          'Treat context sovereignty as a requirement, whoever you buy from or whatever you build.',
        type: 'section',
      },
      {
        id: 'chapter-5-which-one-do-you-need',
        label: 'Which one?',
        title: 'Which one do you need?',
        type: 'section',
      },
    ],
  },
  {
    id: 'chapter-7',
    number: 7,
    slug: 'chapter-7',
    title: 'What the builders see coming',
    navTitle: 'Future',
    subheading:
      'On the takes every builder shares, and the questions none of them can answer.',
    intro: [
      "A consolidation of which parts the vendors have consensus on, and what they don't.",
    ],
    anchors: [
      {
        id: 'chapter-6-intro',
        label: 'Introduction',
        title: 'Intro',
        type: 'section',
      },
      {
        id: 'chapter-6-intro-2-you-ll-maintain-the-company',
        label: "You'll maintain the Company",
        title: "You'll maintain the Company Brain forever",
        type: 'section',
      },
      {
        id: 'chapter-6-intro-11-ai-is-not-ready',
        label: 'AI is not ready',
        title:
          'AI is not ready for autonomously writing to your Knowledge Base',
        type: 'section',
      },
      {
        id: 'chapter-6-intro-18-capture-has-to-happen',
        label: 'Capture has to happen',
        title: 'Capture has to happen at the place of work',
        type: 'section',
      },
      {
        id: 'chapter-6-intro-25-people-will-keep-asking',
        label: 'People will keep asking',
        title:
          'People will keep asking the brain questions. Agents will ask more.',
        type: 'section',
      },
      {
        id: 'chapter-6-what-nobody-can-answer-yet',
        label: 'Open questions',
        title: 'What nobody can answer yet',
        type: 'section',
      },
      {
        id: 'chapter-6-what-nobody-can-answer-yet-1-how-does-context-from',
        label: 'How does context from',
        title: 'How does context from personal AI chats reach the brain?',
        type: 'section',
      },
      {
        id: 'chapter-6-what-nobody-can-answer-yet-6-when-two-versions-conflict',
        label: 'When two versions conflict,',
        title: 'When two versions conflict, whose truth does the brain keep?',
        type: 'section',
      },
      {
        id: 'chapter-6-what-nobody-can-answer-yet-10-when-does-the-brain-write',
        label: 'When can it write?',
        title: 'When does the brain get to write itself?',
        type: 'section',
      },
      {
        id: 'chapter-6-what-nobody-can-answer-yet-14-how-do-you-get-80',
        label: 'The unwritten 80%',
        title: 'How do you get the 80% that was never written down?',
        type: 'section',
      },
      {
        id: 'chapter-7-a-note-from-chris',
        label: 'A note from Chris',
        title: 'A note from Chris',
        type: 'section',
      },
    ],
  },
]
